import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Calendar,
  Clock,
  Tag,
  ArrowRight,
  TrendingUp,
  PiggyBank,
  Calculator,
  Shield,
  DollarSign,
  Home,
  CreditCard,
  Building,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import NewsletterSignup from '../components/NewsletterSignup';
import { blogPostMeta as blogPosts, categories } from '../data/blogPosts';
import { useSEO } from '../hooks/useSEO';

const POSTS_PER_PAGE = 12;

const Blog: React.FC = () => {
  useSEO({
    title: 'Blog de Finanças Pessoais, Investimentos e Economia 2026 | O Teu Dinheiro',
    description: 'Artigos práticos e análises atualizadas sobre investimentos, cartões de alta renda, renda fixa (LCI/LCA/CDB), ETFs, Reforma Tributária e economia doméstica.',
    keywords: 'blog finanças pessoais, artigos de investimentos, educação financeira 2026, renda fixa, cartões black, reforma tributária',
    url: 'https://www.oteudinheiro.com.br/blog'
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Read tag and page from URL
  const activeTag = searchParams.get('tag') || '';
  const urlPage = parseInt(searchParams.get('page') || '1', 10);

  useEffect(() => {
    setCurrentPage(urlPage);
  }, [urlPage]);

  const clearTag = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('tag');
    newParams.delete('page');
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('page');
    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    const newParams = new URLSearchParams(searchParams);
    if (page > 1) {
      newParams.set('page', String(page));
    } else {
      newParams.delete('page');
    }
    setSearchParams(newParams);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'Todos' || post.category === selectedCategory;
    const matchesSearch = searchTerm === '' || post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesTag = activeTag === '' || post.tags.some(tag => tag.toLowerCase() === activeTag.toLowerCase());
    return matchesCategory && matchesSearch && matchesTag;
  }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  // Pagination — exclude featured posts from main list on page 1
  const featuredPosts = blogPosts.filter(post => post.featured)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 4);
  const featuredIds = new Set(featuredPosts.map(p => p.id));
  const nonFeaturedFiltered = selectedCategory === 'Todos' && currentPage === 1
    ? filteredPosts.filter(p => !featuredIds.has(p.id))
    : filteredPosts;
  const totalPages = Math.ceil(nonFeaturedFiltered.length / POSTS_PER_PAGE);
  const paginatedPosts = nonFeaturedFiltered.slice(
    (currentPage - 1) * POSTS_PER_PAGE,
    currentPage * POSTS_PER_PAGE
  );

  const recentPosts = [...blogPosts].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 3);

  const categoryIcons: { [key: string]: React.ReactNode } = {
    'Dívidas': <Calculator className="w-4 h-4" />,
    'PIX': <Shield className="w-4 h-4" />,
    'Investimentos': <TrendingUp className="w-4 h-4" />,
    'Economia': <PiggyBank className="w-4 h-4" />,
    'Bancos Digitais': <CreditCard className="w-4 h-4" />,
    'Cartão de Crédito': <CreditCard className="w-4 h-4" />,
    'Planejamento': <Home className="w-4 h-4" />,
    'Impostos': <DollarSign className="w-4 h-4" />,
    'Imóveis': <Building className="w-4 h-4" />,
    'Ações': <TrendingUp className="w-4 h-4" />,
    'Criptomoedas': <Shield className="w-4 h-4" />,
    'Renda Fixa': <DollarSign className="w-4 h-4" />
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-700 text-white py-20">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Blog O Teu Dinheiro
            </h1>
            <p className="text-xl text-primary-100 mb-8">
              Dicas práticas e guias completos para organizar suas finanças pessoais
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <input
                type="text"
                placeholder="Buscar artigos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-6 py-3 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white"
                aria-label="Buscar artigos"
              />
              <button className="btn btn-secondary bg-white text-primary-600 hover:bg-gray-100">
                Buscar
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-8 bg-white border-b">
        <div className="container-custom">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <button
                key={category.name}
                onClick={() => handleCategoryChange(category.name)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${selectedCategory === category.name
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                {category.name !== 'Todos' && categoryIcons[category.name]}
                <span>{category.name}</span>
                <span className="bg-white bg-opacity-20 px-2 py-1 rounded-full text-xs">
                  {category.count}
                </span>
              </button>
            ))}
          </div>
          {/* Active Tag Filter */}
          {activeTag && (
            <div className="flex justify-center mt-4">
              <span className="inline-flex items-center gap-2 bg-primary-100 text-primary-700 px-4 py-2 rounded-full text-sm font-medium">
                <Tag className="w-4 h-4" />
                Tag: {activeTag}
                <button onClick={clearTag} className="hover:bg-primary-200 rounded-full p-0.5 transition-colors" aria-label="Limpar filtro de tag">
                  <X className="w-4 h-4" />
                </button>
              </span>
            </div>
          )}
        </div>
      </section>

      <div className="container-custom py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Featured Posts — only on page 1 */}
            {selectedCategory === 'Todos' && currentPage === 1 && (
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <TrendingUp className="w-6 h-6 mr-2 text-primary-600" />
                  Artigos em Destaque
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  {featuredPosts.map((post) => (
                    <article key={post.id} className="card p-6 hover:shadow-xl transition-shadow">
                      <div className="flex items-center space-x-2 text-sm text-gray-500 mb-3">
                        <Tag className="w-4 h-4" />
                        <span className="bg-primary-100 text-primary-600 px-2 py-1 rounded-full text-xs">
                          {post.category}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <div className="flex items-center space-x-4">
                          <span className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            {new Date(post.date).toLocaleDateString('pt-BR')}
                          </span>
                          <span className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {post.readTime}
                          </span>
                        </div>
                        <Link
                          to={`/blog/${post.id}`}
                          className="text-primary-600 hover:text-primary-700 font-medium flex items-center"
                        >
                          Ler mais
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </Link>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            )}

            {/* All Posts */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {selectedCategory === 'Todos' ? 'Todos os Artigos' : `Artigos de ${selectedCategory}`}
              </h2>
              <div className="space-y-6">
                {paginatedPosts.map((post) => (
                  <article key={post.id} className="card p-6 hover:shadow-xl transition-shadow">
                    <div className="flex items-center space-x-2 text-sm text-gray-500 mb-3">
                      <Tag className="w-4 h-4" />
                      <span className="bg-primary-100 text-primary-600 px-2 py-1 rounded-full text-xs">
                        {post.category}
                      </span>
                      {post.featured && (
                        <span className="bg-yellow-100 text-yellow-600 px-2 py-1 rounded-full text-xs">
                          Destaque
                        </span>
                      )}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center space-x-4">
                        <span className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {new Date(post.date).toLocaleDateString('pt-BR')}
                        </span>
                        <span className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {post.readTime}
                        </span>
                      </div>
                      <Link
                        to={`/blog/${post.id}`}
                        className="text-primary-600 hover:text-primary-700 font-medium flex items-center"
                      >
                        Ler mais
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Link>
                    </div>
                  </article>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Anterior
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${page === currentPage
                        ? 'bg-primary-600 text-white'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Próximo
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-8">
              {/* Recent Posts */}
              <div className="card p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  Artigos Recentes
                </h3>
                <div className="space-y-4">
                  {recentPosts.map((post) => (
                    <Link
                      key={post.id}
                      to={`/blog/${post.id}`}
                      className="block hover:bg-gray-50 p-3 rounded-lg transition-colors"
                    >
                      <h4 className="font-medium text-gray-900 mb-1 line-clamp-2">
                        {post.title}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {new Date(post.date).toLocaleDateString('pt-BR')}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Categories */}
              <div className="card p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  Categorias
                </h3>
                <div className="space-y-2">
                  {categories.slice(1).map((category) => (
                    <button
                      key={category.name}
                      onClick={() => setSelectedCategory(category.name)}
                      className={`w-full flex items-center justify-between p-2 rounded-lg text-left transition-colors ${selectedCategory === category.name
                        ? 'bg-primary-50 text-primary-600'
                        : 'hover:bg-gray-50'
                        }`}
                    >
                      <div className="flex items-center space-x-2">
                        {categoryIcons[category.name]}
                        <span>{category.name}</span>
                      </div>
                      <span className="text-sm text-gray-500">{category.count}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Newsletter */}
              <div className="mt-8">
                <NewsletterSignup variant="sidebar" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Blog;